import { useQuery } from "@tanstack/react-query";
import {
  supplyPlanRepository,
  type SupplyPlanResponse,
} from "../repositories/supplyPlanRepository";

export type SupplyPlanSummary = {
  stock: SupplyPlanResponse["stock"];
  demand: SupplyPlanResponse["demand"];
  costs?: SupplyPlanResponse["costs"];
  itemsToBuyCount: number;
  readinessSummary?: string;
  missionsCount?: number;
  coveragePercent?: number;
  itemsWithoutCostCount?: number;
  topItems: Array<{
    id: string;
    name: string;
    toBuyQuantity: number;
    unit: string;
    estimatedCost?: number;
    imageUrl?: string;
    costSource?: "purchase_history" | "manual" | null;
  }>;
};

export const supplyPlanQueryKey = (terreiroId?: string, mediumId?: string) =>
  terreiroId
    ? (["terreiro", terreiroId, "supply-plan"] as const)
    : (["medium", mediumId, "supply-plan"] as const);

export const mapSupplyPlanToSummary = (
  plan: SupplyPlanResponse,
): SupplyPlanSummary => {
  const itemsToBuy = plan.items.filter((item) => item.toBuyQuantity > 0);

  return {
    stock: plan.stock,
    demand: plan.demand,
    costs: plan.costs,
    itemsToBuyCount: itemsToBuy.length,
    readinessSummary: plan.readiness?.summary,
    missionsCount: plan.missions?.length ?? 0,
    coveragePercent: plan.costs?.coveragePercent,
    itemsWithoutCostCount: plan.costs?.itemsWithoutCostCount,
    topItems: itemsToBuy
      .sort((a, b) => b.toBuyQuantity - a.toBuyQuantity)
      .slice(0, 5)
      .map((item) => ({
        id: item.id,
        name: item.name,
        toBuyQuantity: item.toBuyQuantity,
        unit: item.unit,
        estimatedCost: item.estimatedCost,
        imageUrl: item.imageUrl ?? undefined,
        costSource: item.costSource ?? null,
      })),
  };
};

type SupplyPlanQueryClient = {
  invalidateQueries: (options: {
    queryKey?: readonly unknown[];
    predicate?: (query: { queryKey: unknown }) => boolean;
  }) => void | Promise<unknown>;
};

export const invalidateSupplyPlanQueries = (
  queryClient: SupplyPlanQueryClient,
  terreiroId?: string,
  mediumId?: string,
) => {
  if (terreiroId) {
    queryClient.invalidateQueries({
      queryKey: supplyPlanQueryKey(terreiroId),
    });
    return;
  }

  if (mediumId) {
    queryClient.invalidateQueries({
      queryKey: supplyPlanQueryKey(undefined, mediumId),
    });
  }
};

export const invalidateAllSupplyPlanQueries = (
  queryClient: SupplyPlanQueryClient,
) => {
  return queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      return Array.isArray(key) && key.includes("supply-plan");
    },
  });
};

export function useSupplyPlanQuery({
  terreiroId,
  mediumId,
  enabled = true,
}: {
  terreiroId?: string;
  mediumId?: string;
  enabled?: boolean;
}) {
  const hasScope = Boolean(terreiroId || mediumId);

  return useQuery({
    queryKey: hasScope
      ? supplyPlanQueryKey(terreiroId, mediumId)
      : (["supply-plan", "none"] as const),
    queryFn: () => supplyPlanRepository.getSupplyPlan(terreiroId, mediumId),
    enabled: enabled && hasScope,
    staleTime: 1000 * 60 * 2,
    gcTime: 60 * 60 * 1000,
    retry: 2,
    refetchOnMount: "always",
  });
}
